import React, { createContext, ReactNode, useContext, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { createPortal } from "react-dom";

interface SignupOptions {
  purpose?: 'generate' | 'report-export';
  returnTo?: string;
  beforeRedirect?: () => void;
}

interface PublicTrialContextValue {
  isPublicTrial: boolean;
  requestTrialSignup: (options?: SignupOptions) => void;
}

const PublicTrialContext = createContext<PublicTrialContextValue>({
  isPublicTrial: false,
  requestTrialSignup: () => undefined,
});

interface PublicTrialProviderProps {
  children: ReactNode;
}

export const PublicTrialProvider: React.FC<PublicTrialProviderProps> = ({ children }) => {
  const { loginWithRedirect } = useAuth0();
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [options, setOptions] = useState<SignupOptions>({});
  const [error, setError] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isReportExport = options.purpose === 'report-export';

  const requestTrialSignup = (nextOptions: SignupOptions = {}) => {
    setOptions(nextOptions);
    setError('');
    setIsPromptOpen(true);
  };

  const authenticate = async (signup: boolean) => {
    setIsRedirecting(true);
    setError('');
    try {
      options.beforeRedirect?.();
      await loginWithRedirect({
        appState: { returnTo: options.returnTo || '/dashboard' },
        authorizationParams: signup ? { screen_hint: 'signup' } : { prompt: 'login' },
      });
    } catch {
      setError('Could not continue to sign-in. Your comments are still here. Please try again.');
    } finally {
      setIsRedirecting(false);
    }
  };

  const trialPrompt = (
    <div className="public-trial-modal" role="dialog" aria-modal="true" aria-labelledby="public-trial-title">
      <div className="public-trial-modal__backdrop" onClick={() => setIsPromptOpen(false)} />
      <div className="public-trial-modal__content">
        <button
          type="button"
          className="public-trial-modal__close"
          onClick={() => setIsPromptOpen(false)}
          aria-label="Close signup prompt"
        >
          x
        </button>
        <p className="public-trial-modal__eyebrow">Free plan</p>
        <h2 id="public-trial-title">{isReportExport ? 'Sign in to copy or download' : 'Sign up free to generate'}</h2>
        <p>
          <span>{isReportExport ? 'Your comments are ready. Create a free account or sign in to use them.' : 'Create teaching resources with a free monthly allowance.'}</span>
          <span className="public-trial-modal__subcopy">No payment details required.</span>
        </p>
        <div className="public-trial-modal__actions">
          <button type="button" className="public-trial-modal__primary" disabled={isRedirecting} onClick={() => void authenticate(true)}>
            Create free account
          </button>
          {isReportExport && <button type="button" className="public-trial-modal__secondary" disabled={isRedirecting}
            onClick={() => void authenticate(false)}>Sign in</button>}
          <button
            type="button"
            className="public-trial-modal__secondary"
            onClick={() => setIsPromptOpen(false)}
          >
            Keep exploring
          </button>
        </div>
        {error && <p role="alert">{error}</p>}
      </div>
    </div>
  );

  return (
    <PublicTrialContext.Provider value={{ isPublicTrial: true, requestTrialSignup }}>
      {children}
      {isPromptOpen && createPortal(trialPrompt, document.body)}
    </PublicTrialContext.Provider>
  );
};

export const usePublicTrial = () => useContext(PublicTrialContext);
